import {
  ArrowRightOutlined,
  CalendarOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import {
  Image,
  Tag,
  Tooltip,
  Typography,
  theme,
  Space,
  Table,
  DatePicker,
} from "antd";
import React, { useRef } from "react";
import useIsMobile from "../../hooks/useIsMobile";
import usePageFillTableScroll from "../../hooks/usePageFillTableScroll";
import bedImg from "../../assets/images/patientIcons/bed.svg";
import docIcon from "../../assets/doctorIcons/consultant.svg";
import dayjs from "dayjs";
import tagStyles from "../../styles/Custom/Tag.module.css";
import NoDataPage from "../NoDataPages/NoDataPage";
import PatientDataIcon from "../../assets/images/nurseIcons/patientdata.svg";
import { capitalizeFirstLetter } from "../../helpers/utility";

const { useToken } = theme;

const NutritionistPatientTable = ({
  patientList,
  loading,
  onPatientClick,
  onAssessmentDateChange,
  assessmentDate,
  activeInSegment,
}) => {
  const { token } = useToken();
  const isMobile = useIsMobile();
  const tableWrapRef = useRef(null);
  const tableScroll = usePageFillTableScroll(
    tableWrapRef,
    isMobile,
    "default",
    patientList?.data?.length ?? 0
  );

  const truncateName = (name, role) => {
    if (role === "patient") {
      return name && name.length > 20 ? `${name.slice(0, 20)}...` : name;
    } else if (role === "consultant") {
      return name && name.length > 12 ? `${name.slice(0, 12)}...` : name;
    }
    return name;
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "patient_name",
      key: "patient_name",
      render: (_, item) => {
        const age = item?.patient_age_by_dob;
        const gender = item?.patient_gender;
        const genderShort =
          gender === "Male"
            ? "M"
            : gender === "Female"
            ? "F"
            : gender === "Other"
            ? "Other"
            : "";
        let ageText = "";
        if (age != null) {
          if (age < 1) {
            ageText = `${Math.round(age * 12)} months`;
          } else {
            ageText = `${Math.floor(age)}`;
          }
        }
        const ageGender = [ageText, genderShort].filter(Boolean).join(" / ");

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              maxWidth: 200,
              cursor: "pointer",
            }}
          >
            <Typography.Text strong>
              {truncateName(item?.patient_name, "patient")}
            </Typography.Text>
            <Space size={4}>
              {ageGender && <Tag>{ageGender}</Tag>}
              {item?.bed_name && (
                <Tag color="orange" style={{ width: "100%" }}>
                  <Image src={bedImg} preview={false} /> {item?.bed_name}
                </Tag>
              )}
            </Space>
          </div>
        );
      },
    },
    {
      title: "IP ID",
      dataIndex: "ip_id",
      key: "ip_id",
      render: (ip_id) => (ip_id ? <Tag>IP ID: {ip_id}</Tag> : null),
    },
    {
      title: "Diet Type",
      dataIndex: "diet_type",
      key: "diet_type",
      width: 100,
      render: (diet_type) =>
        diet_type ? (
          <Tooltip title={diet_type}>
            <Tag color="green">{capitalizeFirstLetter(diet_type)}</Tag>
          </Tooltip>
        ) : null,
    },
    {
      title: "DOA",
      dataIndex: "date_of_admission",
      key: "date_of_admission",
      render: (_, item) => (
        <Tag
          className={tagStyles.smTag}
          style={{
            color: token.colorPrimary,
            borderColor:
              "color-mix(in srgb, var(--ant-primary-color) 24%, white)",
            backgroundColor:
              "color-mix(in srgb, var(--ant-primary-color) 10%, white)",
          }}
        >
          <CalendarOutlined style={{ marginRight: "4px" }} />
          {item?.discharge_date
            ? `${dayjs(item?.date_of_admission).format(
                "DD MMM, YYYY"
              )} - ${dayjs(item?.discharge_date).format("DD MMM, YYYY")}`
            : dayjs(item?.date_of_admission).format("DD MMM, YYYY")}
        </Tag>
      ),
    },
    {
      title: "Practitioner",
      dataIndex: "doctor_name",
      key: "doctor_name",
      render: (doctor_name) =>
        doctor_name ? (
          <Tooltip
            placement="top"
            title={doctor_name}
            color="#E6F4FF"
            overlayInnerStyle={{ color: "#0d439e" }}
          >
            <Tag color="blue" style={{ cursor: "pointer" }}>
              <Image
                src={docIcon}
                style={{ width: "12px", marginRight: "4px" }}
                preview={false}
              />
              {truncateName(doctor_name, "consultant")}
            </Tag>
          </Tooltip>
        ) : null,
    },

    ...(activeInSegment === "current"
      ? [
          {
            title: (
              <div
                style={{ display: "flex", alignItems: "center", gap: 4 }}
                onClick={(e) => e.stopPropagation()}
              >
                <span>Assessment Date</span>

                {/*
                  Key fix: render DatePicker as a styled icon.
                  - No controlled `open` prop — Ant Design manages open/close internally.
                  - Input is hidden via CSS overflow trick; only the suffixIcon is visible.
                  - Clicking the icon area naturally opens the picker every time.
                */}
                <div
                  style={{
                    position: "relative",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <DatePicker
                    format="DD MMM, YYYY"
                    value={assessmentDate ? dayjs(assessmentDate) : null}
                    onChange={(date) => onAssessmentDateChange(date || null)}
                    allowClear={false}
                    suffixIcon={
                      <CalendarOutlined
                        style={{
                          color: assessmentDate
                            ? token.colorPrimary
                            : token.colorTextSecondary,
                          fontSize: 14,
                          pointerEvents: "none",
                        }}
                      />
                    }
                    style={{
                      // Collapse the input, show only the suffix icon
                      width: 22,
                      padding: 0,
                      border: "none",
                      boxShadow: "none",
                      background: "transparent",
                      overflow: "hidden",
                    }}
                    className="date-picker-icon-only"
                    inputReadOnly
                  />
                </div>

                {assessmentDate && (
                  <CloseCircleOutlined
                    onClick={(e) => {
                      e.stopPropagation();
                      onAssessmentDateChange(null);
                    }}
                    style={{
                      cursor: "pointer",
                      color: "red",
                      fontSize: 12,
                    }}
                  />
                )}
              </div>
            ),
            dataIndex: "next_assessment_date",
            key: "next_assessment_date",
            render: (next_assessment_date) =>
              next_assessment_date ? (
                <Tag color="cyan">
                  <CalendarOutlined style={{ marginRight: "4px" }} />
                  {dayjs(next_assessment_date).format("DD MMM, YYYY")}
                </Tag>
              ) : null,
          },
        ]
      : []),
    {
      title: "",
      key: "action",
      render: (_, item) => (
        <Typography.Link
          style={{ color: token.colorPrimary }}
          onClick={(e) => {
            e.stopPropagation();
            onPatientClick(item);
          }}
        >
          View EMR <ArrowRightOutlined />
        </Typography.Link>
      ),
    },
  ];

  return (
    <div ref={tableWrapRef} className="ui-table-fill-body">
      <Table
        className="patient-list no-vertical-borders"
        loading={loading}
        columns={columns}
        dataSource={patientList?.data}
        rowKey="id"
        pagination={false}
        scroll={tableScroll}
        onRow={(item) => ({
          onClick: () => onPatientClick(item),
          style: { cursor: "pointer" },
        })}
        locale={{
          emptyText: (
            <NoDataPage
              title="No In Patients Available"
              subtitle="Please check back later or adjust your filters."
              image={PatientDataIcon}
            />
          ),
        }}
      />
    </div>
  );
};

export default NutritionistPatientTable;
